import * as React from "react";
import {Badge} from "react-bootstrap";
import renderUtils from "../../../helpers/renderUtils";
import PartListActions from "../../management/actions/PartsListActions";
import {DaysSinceLastCleaningBadge} from "../../management/actions/DaysSinceLastCleaningBadge";
import PartEdition from "../part/PartEdition";
import {hasFormatsFullAccess} from "../../../helpers/jwt";
import { useTranslation } from 'react-i18next';

export const usePartsTableColumns = (extended, short, twin, cleaning) => {
    const { t } = useTranslation();
    
    return [
        {
            label: "",
            field: "checked",
            sort: "desc",
            type: "checkbox",
            width: 200,
            hideForReadonly: true,
            twin: true
        },
        {
            label: "ID",
            field: "id",
            sort: "desc",
            width: 200,
            twin: true,
            type: "badge"
        },
        {
            label: t("common.part_name"),
            field: "name",
            sort: "desc",
            style: {minWidth: '150px'},
            short: true,
            twin: true,
            type: "tooltipImage",
            resolveImage: function (row) {
                return row.files[0];
            },
        },
        {
            label: t("object_types.machine"),
            field: "machineType",
            sort: "desc",
            twin: true,
            width: 200,
            type: "custom",
            custom: function (column, row, history, internalReload, setInternalReload, setLoader, disableRule, externalState) {
                return <Badge
                    className={"outline-secondary font-max"}> {renderUtils.renderMachineType({machineType: row.machineType}, t)}</Badge>
            }
        },
        {
            label: t("forms.category_name"),
            field: "category",
            sort: "desc",
            width: 200,
        },
        {
            label: t("menu.cleaning"),
            field: "lastCleaningDate",
            sort: "desc",
            width: 200,
            cleaning: true,
            type: "custom",
            custom: function (column, row, history, internalReload, setInternalReload, setLoader, disableRule, externalState) {
                return row.lastCleaningDate && <div className={"d-flex"}>
                    <Badge className={"outline-secondary font-max mr-1"}> {row.lastCleaningDate}</Badge>
                    <DaysSinceLastCleaningBadge part={row} cleaningValidDuration={externalState} placement={"bottom"}/>
                </div>
            }
        },
        {
            label: t("common.qr_code"),
            field: "qrCode",
            sort: "desc",
            width: 200,
            type: "qrCode",
            qrCode: "id",
            extended: true
        },
        {
            field: "type",
            label: t("forms.type"),
            sort: "desc",
            width: 200,
            extended: true,
            type: "custom",
            custom: function (column, row, history, internalReload, setInternalReload, setLoader, disableRule, externalState) {
                return row.type && <Badge
                    className={"outline-secondary font-max"}> {renderUtils.renderEnumName(row.type, "enums/partType", t)}</Badge>
            }
        },
        {
            field: "partNumber",
            label: t("common.part_number"),
            sort: "desc",
            width: 200,
            extended: true
        },
        {
            field: "position",
            label: t("common.position"),
            sort: "desc",
            width: 200,
            extended: true
        },
        {
            field: "label",
            label: t("common.comment"),
            sort: "desc",
            width: 200,
            extended: true,
            type: "textarea"
        },
        {
            label: t("common.status"),
            field: "status",
            short: true,
            sort: "desc",
            width: 200,
            type: "custom",
            twin: true,
            hideForReadonly: true,
            custom: function (column, row, history, internalReload, setInternalReload, setLoader, disableRule, externalState) {
                return renderUtils.renderPartStatus(row.status, "", false, t)
            }
        },
        {
            field: "actions",
            label: t("forms.actions"),
            sort: "desc",
            width: 30,
            type: "custom",
            extended: true,
            hasAccess: () => hasFormatsFullAccess(),
            custom: function (column, row, history, reload, setReload, setLoader, disableRule) {

                return <>
                    {row.status === "WITHDRAWN" && <PartListActions part={row}/>}
                    {row.status !== "WITHDRAWN" && extended && <PartEdition
                        setLoader={setLoader}
                        setReload={setReload}
                        reload={reload}
                        part={row}/>}
                </>

            }
        }
    ]
        .filter(c => extended ? true : !c.extended)
        .filter(c => short ? c.short : true)
        .filter(c => c.cleaning ? cleaning : true)
        .filter(c => twin ? c.twin : true);
};

// Legacy export for backward compatibility
const columns = (extended, short, twin, cleaning, t) => {
    return [
        {
            label: "",
            field: "checked",
            sort: "desc",
            type: "checkbox",
            width: 200,
            hideForReadonly: true,
            twin: true
        },
        {
            label: "ID",
            field: "id",
            sort: "desc",
            width: 200,
            twin: true,
            type: "badge"
        },
        {
            label: t ? t("common.part_name") : "Nazwa części",
            field: "name",
            sort: "desc",
            style: {minWidth: '150px'},
            short: true,
            twin: true,
            type: "tooltipImage",
            resolveImage: function (row) {
                return row.files[0];
            },
        },
        {
            label: t ? t("common.machine") : "Maszyna",
            field: "machineType",
            sort: "desc",
            twin: true,
            width: 200,
            type: "custom",
            custom: function (column, row, history, internalReload, setInternalReload, setLoader, disableRule, externalState) {
                return <Badge
                    className={"outline-secondary font-max"}> {renderUtils.renderMachineType({machineType: row.machineType})}</Badge>
            }
        },
        {
            label: t ? t("common.category") : "Kategoria",
            field: "category",
            sort: "desc",
            width: 200,
        },
        {
            label: t ? t("common.last_cleaning") : "Ostatnie czyszczenie",
            field: "lastCleaningDate",
            sort: "desc",
            width: 200,
            cleaning: true,
            type: "custom",
            custom: function (column, row, history, internalReload, setInternalReload, setLoader, disableRule, externalState) {
                return row.lastCleaningDate && <div className={"d-flex"}>
                    <Badge className={"outline-secondary font-max mr-1"}> {row.lastCleaningDate}</Badge>
                    <DaysSinceLastCleaningBadge part={row} cleaningValidDuration={externalState} placement={"bottom"}/>
                </div>
            }
        },
        {
            label: t ? t("common.qr_code") : "Kod QR",
            field: "qrCode",
            sort: "desc",
            width: 200,
            type: "qrCode",
            qrCode: "id",
            extended: true
        },
        {
            field: "type",
            label: t ? t("common.kind") : "Rodzaj",
            sort: "desc",
            width: 200,
            extended: true,
            type: "custom",
            custom: function (column, row, history, internalReload, setInternalReload, setLoader, disableRule, externalState) {
                return row.type && <Badge
                    className={"outline-secondary font-max"}> {renderUtils.renderEnumName(row.type, "enums/partType", t)}</Badge>
            }
        },
        {
            field: "partNumber",
            label: t ? t("common.number") : "Numer",
            sort: "desc",
            width: 200,
            extended: true
        },
        {
            field: "position",
            label: t ? t("common.position") : "Położenie",
            sort: "desc",
            width: 200,
            extended: true
        },
        {
            field: "label",
            label: t ? t("common.comment") : "Komentarz",
            sort: "desc",
            width: 200,
            extended: true,
            type: "textarea"
        },
        {
            label: t ? t("common.status") : "Status",
            field: "status",
            short: true,
            sort: "desc",
            width: 200,
            type: "custom",
            twin: true,
            hideForReadonly: true,
            custom: function (column, row, history, internalReload, setInternalReload, setLoader, disableRule, externalState) {
                return renderUtils.renderPartStatus(row.status, "", false, t    )
            }
        },
        {
            field: "actions",
            label: t ? t("common.actions") : "Akcje",
            sort: "desc",
            width: 30,
            type: "custom",
            extended: true,
            hasAccess: () => hasFormatsFullAccess(),
            custom: function (column, row, history, reload, setReload, setLoader, disableRule) {

                return <>
                    {row.status === "WITHDRAWN" && <PartListActions part={row}/>}
                    {row.status !== "WITHDRAWN" && extended && <PartEdition
                        setLoader={setLoader}
                        setReload={setReload}
                        reload={reload}
                        part={row}/>}
                </>

            }
        }
    ]
        .filter(c => extended ? true : !c.extended)
        .filter(c => short ? c.short : true)
        .filter(c => c.cleaning ? cleaning : true)
        .filter(c => twin ? c.twin : true)
};

export default columns;